var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { classMap, html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { CarouselLayoutEnum } from '@inter-ikea-kompis/component-carousel';
import { Carousel } from '@inter-ikea-kompis/component-carousel';
import { Image } from '@inter-ikea-kompis/component-image';
import { ImageAspectRatioEnum } from '@inter-ikea-kompis/component-image';
import { Button as SkapaButton, IconButton as SkapaIconButton } from '@ingka/button-webc/passive.js';
import { Carousel as SkapaCarousel } from '@ingka/carousel-webc/passive.js';
import { SkipContent as SkapaSkipContent } from '@ingka/skip-content-webc/passive.js';
import MediaCarouselStyle from './MediaCarouselStyle.js';
const PRODUCT_IMAGE_SCALE = 1.04;
/**
 * The Media Carousel displays any type of media, e.g. images and videos, in a carousel.
 * It can be accomplished by a carousel scrollbar or together with corresponding thumbnails.
 *
 * @event change Media changed
 * @event showAllThumbnails Show all thumbnails event.
 */
export default class MediaCarousel extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // Array of MediaItem objects
        this.mediaItems = null;
        // Can be used to set custom start image index for the main image or to control the carousel selected state.
        this.selectedIndex = 0;
        // Visibility of scrollbar
        this.showScrollbar = false;
        // Visibility of arrow controls on mobile viewport
        this.showArrows = false;
        // Expanding thumbnails
        this.showAll = false;
        // Max limit of thumbnails to be displayed.
        this.thumbnailLimit = null;
        // Layout
        this.layout = CarouselLayoutEnum.default;
        // Skip listing label
        this.skipListingLabel = null;
        // Show less label
        this.showLessLabel = null;
        // The aspect ratio that the image should adopt.
        this.aspectRatio = ImageAspectRatioEnum.ORIGINAL;
    }
    render() {
        // We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslations()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        return html `
			<style>
				${MediaCarouselStyle.getStyle({
            layout: this.layout,
            theme: this.theme
        })}
			</style>

			${this.getGallery()} ${this.mediaPicker()}
			<div
				id="carouselCustomSkipTarget"
				@focusout="${(event) => {
            const target = event.currentTarget;
            if (target) {
                target.tabIndex = -1;
            }
        }}"
			></div>
		`;
    }
    async connectedCallback() {
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        this.customElements.define('skapa-carousel', SkapaCarousel);
        this.customElements.define('skapa-skip-content', SkapaSkipContent);
        this.customElements.define('kompis-carousel', Carousel);
        this.customElements.define('kompis-image', Image);
        super.connectedCallback();
    }
    hasTranslations() {
        return !!this.translations || !!this.localizedInformation?.translations;
    }
    getTranslations() {
        const translations = this.translations
            ? this.translations
            : this.localizedInformation?.translations;
        return translations;
    }
    onThumbnailClick(selectedIndex) {
        this.selectedIndex = selectedIndex;
    }
    updateVisibleSlide(event) {
        // Defaults to selectedIndex since index might be undefined if the user clicks arrows too fast.
        const index = event.detail[0] ?? this.selectedIndex;
        this.onThumbnailClick(index);
        this.dispatchEvent(new CustomEvent('change', {
            bubbles: true,
            detail: { selectedIndex: index, type: 'thumbnail' }
        }));
    }
    getSkipListing() {
        if (!this.skipListingLabel) {
            return null;
        }
        // Find and set the element to be tabbable, and when the element is focused out, the tabIndex is removed from the element.
        const handleSkipListing = (event) => {
            event.preventDefault();
            const focusElement = this.shadowRoot?.getElementById('carouselCustomSkipTarget');
            focusElement.tabIndex = 0;
            focusElement.focus();
        };
        return html `
			<skapa-skip-content slot="skip-button">
				<a href="#" @click="${handleSkipListing.bind(this)}">${this.skipListingLabel}</a>
			</skapa-skip-content>
		`;
    }
    getGallery() {
        return this.layout === CarouselLayoutEnum.overflow
            ? html `
					<skapa-overflow-carousel
						slide-index="${this.selectedIndex}"
						?hidden-scrollbar="${!this.showScrollbar}"
						?slide-show="${this.showArrows}"
						@skapa-carousel-visible-slides="${(event) => this.updateVisibleSlide(event)}"
					>
						${this.getSkipListing()} ${this.getCarouselItems()}
					</skapa-overflow-carousel>
				`
            : html `
					<skapa-carousel
						slide-index="${this.selectedIndex}"
						?hidden-scrollbar="${!this.showScrollbar}"
						?slide-show="${this.showArrows}"
						@skapa-carousel-visible-slides="${(event) => this.updateVisibleSlide(event)}"
					>
						${this.getSkipListing()} ${this.getCarouselItems()}
					</skapa-carousel>
				`;
    }
    getCarouselItems() {
        if (!this.mediaItems) {
            return null;
        }
        return this.mediaItems.map((mediaItem, index) => {
            const isAriaHidden = index !== this.selectedIndex;
            // We need to set the muted attribute to true via the loadedmetadata event to avoid sound being played in some browsers anyways.
            if (mediaItem.videoSrc) {
                return html `
					<video
						title="${mediaItem.alt}"
						@loadedmetadata="${(event) => {
                    event.target.muted = true;
                }}"
						muted
						autoplay
						loop
						aria-hidden="${isAriaHidden}"
					>
						<source src="${mediaItem.videoSrc}" type="video/mp4" />
					</video>
				`;
            }
            else {
                return html `
					<kompis-image
						aria-hidden="${isAriaHidden}"
						.src="${mediaItem.src}"
						.alt="${mediaItem.alt}"
						.aspectRatio="${this.aspectRatio}"
						enableSkeletonLoading
					>
					</kompis-image>
				`;
            }
        });
    }
    mediaPicker() {
        if ((this.mediaItems && this.mediaItems.length < 2) || this.showScrollbar) {
            return null;
        }
        return html `
			<div
				class="mediaPicker"
				role="group"
				aria-label="${this.getTranslations().mediaCarouselThumbnailAriaLabel}"
			>
				${this.getThumbnails()}${this.getExpandingThumbnailsButton()}
			</div>
		`;
    }
    getThumbnails() {
        const thumbnails = [];
        const limit = this.showAll || !this.thumbnailLimit
            ? (this.mediaItems?.length ?? 0)
            : Math.min(this.thumbnailLimit, this.mediaItems?.length ?? 0);
        for (let i = 0; i < limit; i++) {
            const mediaItem = this.mediaItems[i];
            const isAriaPressed = i === this.selectedIndex;
            thumbnails.push(html `
				<div
					role="button"
					aria-pressed="${isAriaPressed}"
					@click="${() => this.onThumbnailClick(i)}"
					@keydown="${(event) => {
                if (event.key === 'Enter') {
                    this.onThumbnailClick(i);
                }
            }}"
					tabindex="0"
					class="${classMap({
                thumbnail: true,
                selectedThumbnail: this.selectedIndex === i
            })}"
				>
					<kompis-image
						style="${mediaItem.productImage ? `transform: scale(${PRODUCT_IMAGE_SCALE})` : ''}"
						.src="${mediaItem.thumbnailSrc ? mediaItem.thumbnailSrc : mediaItem.src}"
						.alt="${mediaItem.alt}"
					></kompis-image>
				</div>
			`);
            if (i < limit) {
                thumbnails.push(html `<div class="divider"></div>`);
            }
        }
        return thumbnails;
    }
    getExpandingThumbnailsButton() {
        if (!this.thumbnailLimit || (this.mediaItems?.length ?? 0) <= this.thumbnailLimit) {
            return;
        }
        const secondaryVariant = 'secondary';
        const label = !this.showAll
            ? '+ ' + ((this.mediaItems?.length ?? 0) - this.thumbnailLimit)
            : this.showLessLabel;
        return html `
			<skapa-button
				class="showAllBtn"
				variant="${secondaryVariant}"
				size="small"
				@click="${this.onButtonClick.bind(this)}"
			>
				${label}
			</skapa-button>
		`;
    }
    onButtonClick() {
        this.dispatchEvent(new CustomEvent('showAllThumbnails', { bubbles: true, detail: { showAll: !this.showAll } }));
    }
}
__decorate([
    property({ type: Object, required: true })
], MediaCarousel.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], MediaCarousel.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], MediaCarousel.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], MediaCarousel.prototype, "mediaItems", void 0);
__decorate([
    property({ type: Number })
], MediaCarousel.prototype, "selectedIndex", void 0);
__decorate([
    property({ type: Boolean })
], MediaCarousel.prototype, "showScrollbar", void 0);
__decorate([
    property({ type: Boolean })
], MediaCarousel.prototype, "showArrows", void 0);
__decorate([
    property({ type: Boolean })
], MediaCarousel.prototype, "showAll", void 0);
__decorate([
    property({ type: Number })
], MediaCarousel.prototype, "thumbnailLimit", void 0);
__decorate([
    property({ type: String })
], MediaCarousel.prototype, "layout", void 0);
__decorate([
    property({ type: String })
], MediaCarousel.prototype, "skipListingLabel", void 0);
__decorate([
    property({ type: String })
], MediaCarousel.prototype, "showLessLabel", void 0);
__decorate([
    property({ type: String })
], MediaCarousel.prototype, "aspectRatio", void 0);
//# sourceMappingURL=MediaCarousel.js.map